'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Copy, Key, X, RefreshCw } from 'lucide-react';
import { useToast } from '@/providers/ToastProvider';
import { ApiLogger } from '@/lib/logging/logger'

export default function JwtDebugButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [jwtToken, setJwtToken] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>('');
  const { toast } = useToast();

  useEffect(() => {
    // Only run in development
    if (process.env.NODE_ENV !== 'development') {
      return;
    }
    // Get JWT token from various Supabase storage locations
    const getJwtToken = () => {
      const debugLog: string[] = [];
      
      // Try different Supabase storage patterns
      const patterns = [
        'sb-localhost-auth-token',
        'supabase.auth.token',
        'supabase-auth-token',
        'sb-auth-token'
      ];

      debugLog.push('Checking localStorage...');
      // Check localStorage
      for (const pattern of patterns) {
        const token = localStorage.getItem(pattern);
        if (token) {
          debugLog.push(`Found localStorage key: ${pattern}`);
          try {
            const parsed = JSON.parse(token);
            // Look for access_token in various possible structures
            if (parsed.access_token) {
              debugLog.push(`Found access_token in ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed.access_token;
            }
            if (parsed.currentSession?.access_token) {
              debugLog.push(`Found currentSession.access_token in ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed.currentSession.access_token;
            }
            if (parsed.session?.access_token) {
              debugLog.push(`Found session.access_token in ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed.session.access_token;
            }
            if (typeof parsed === 'string') {
              debugLog.push(`Found string token in ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed;
            }
          } catch {
            // If it's not JSON, it might be the token directly
            if (typeof token === 'string' && token.length > 100) {
              debugLog.push(`Found raw token in ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return token;
            }
          }
        }
      }

      debugLog.push('Checking sessionStorage...');
      // Check sessionStorage
      for (const pattern of patterns) {
        const token = sessionStorage.getItem(pattern);
        if (token) {
          debugLog.push(`Found sessionStorage key: ${pattern}`);
          try {
            const parsed = JSON.parse(token);
            if (parsed.access_token) {
              debugLog.push(`Found access_token in sessionStorage ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed.access_token;
            }
            if (parsed.currentSession?.access_token) {
              debugLog.push(`Found currentSession.access_token in sessionStorage ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed.currentSession.access_token;
            }
            if (parsed.session?.access_token) {
              debugLog.push(`Found session.access_token in sessionStorage ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed.session.access_token;
            }
            if (typeof parsed === 'string') {
              debugLog.push(`Found string token in sessionStorage ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed;
            }
          } catch {
            if (typeof token === 'string' && token.length > 100) {
              debugLog.push(`Found raw token in sessionStorage ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return token;
            }
          }
        }
      }

      debugLog.push('Checking cookies...');
      // Check cookies
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name.includes('supabase') || name.includes('sb-')) {
          debugLog.push(`Found cookie: ${name}`);
          try {
            const parsed = JSON.parse(decodeURIComponent(value));
            if (parsed.access_token) {
              debugLog.push(`Found access_token in cookie ${name}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed.access_token;
            }
            if (parsed.currentSession?.access_token) {
              debugLog.push(`Found currentSession.access_token in cookie ${name}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed.currentSession.access_token;
            }
            if (parsed.session?.access_token) {
              debugLog.push(`Found session.access_token in cookie ${name}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed.session.access_token;
            }
          } catch {
            const decoded = decodeURIComponent(value);
            if (decoded.length > 100) {
              debugLog.push(`Found raw token in cookie ${name}`);
              setDebugInfo(debugLog.join('\n'));
              return decoded;
            }
          }
        }
      }

      debugLog.push('No JWT token found in any location');
      setDebugInfo(debugLog.join('\n'));
      return null;
    };

    setJwtToken(getJwtToken());
  }, []);

  const refreshToken = () => {
    const getJwtToken = () => {
      const debugLog: string[] = [];
      
      // Try different Supabase storage patterns
      const patterns = [
        'sb-localhost-auth-token',
        'supabase.auth.token',
        'supabase-auth-token',
        'sb-auth-token'
      ];

      debugLog.push('Checking localStorage...');
      // Check localStorage
      for (const pattern of patterns) {
        const token = localStorage.getItem(pattern);
        if (token) {
          debugLog.push(`Found localStorage key: ${pattern}`);
          try {
            const parsed = JSON.parse(token);
            // Look for access_token in various possible structures
            if (parsed.access_token) {
              debugLog.push(`Found access_token in ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed.access_token;
            }
            if (parsed.currentSession?.access_token) {
              debugLog.push(`Found currentSession.access_token in ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed.currentSession.access_token;
            }
            if (parsed.session?.access_token) {
              debugLog.push(`Found session.access_token in ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed.session.access_token;
            }
            if (typeof parsed === 'string') {
              debugLog.push(`Found string token in ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed;
            }
          } catch {
            // If it's not JSON, it might be the token directly
            if (typeof token === 'string' && token.length > 100) {
              debugLog.push(`Found raw token in ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return token;
            }
          }
        }
      }

      debugLog.push('Checking sessionStorage...');
      // Check sessionStorage
      for (const pattern of patterns) {
        const token = sessionStorage.getItem(pattern);
        if (token) {
          debugLog.push(`Found sessionStorage key: ${pattern}`);
          try {
            const parsed = JSON.parse(token);
            if (parsed.access_token) {
              debugLog.push(`Found access_token in sessionStorage ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed.access_token;
            }
            if (parsed.currentSession?.access_token) {
              debugLog.push(`Found currentSession.access_token in sessionStorage ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed.currentSession.access_token;
            }
            if (parsed.session?.access_token) {
              debugLog.push(`Found session.access_token in sessionStorage ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed.session.access_token;
            }
            if (typeof parsed === 'string') {
              debugLog.push(`Found string token in sessionStorage ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed;
            }
          } catch {
            if (typeof token === 'string' && token.length > 100) {
              debugLog.push(`Found raw token in sessionStorage ${pattern}`);
              setDebugInfo(debugLog.join('\n'));
              return token;
            }
          }
        }
      }

      debugLog.push('Checking cookies...');
      // Check cookies
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name.includes('supabase') || name.includes('sb-')) {
          debugLog.push(`Found cookie: ${name}`);
          try {
            const parsed = JSON.parse(decodeURIComponent(value));
            if (parsed.access_token) {
              debugLog.push(`Found access_token in cookie ${name}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed.access_token;
            }
            if (parsed.currentSession?.access_token) {
              debugLog.push(`Found currentSession.access_token in cookie ${name}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed.currentSession.access_token;
            }
            if (parsed.session?.access_token) {
              debugLog.push(`Found session.access_token in cookie ${name}`);
              setDebugInfo(debugLog.join('\n'));
              return parsed.session.access_token;
            }
          } catch {
            const decoded = decodeURIComponent(value);
            if (decoded.length > 100) {
              debugLog.push(`Found raw token in cookie ${name}`);
              setDebugInfo(debugLog.join('\n'));
              return decoded;
            }
          }
        }
      }

      debugLog.push('No JWT token found in any location');
      setDebugInfo(debugLog.join('\n'));
      return null;
    };

    setJwtToken(getJwtToken());
  };

  const copyToClipboard = async () => {
    if (jwtToken) {
      try {
        await navigator.clipboard.writeText(jwtToken);
        toast({
          title: 'Copied!',
          description: 'JWT token copied to clipboard',
        });
      } catch (err) {
        ApiLogger.error('Failed to copy token:', err);
        toast({
          title: 'Error',
          description: 'Failed to copy token',
          variant: 'destructive',
        });
      }
    }
  };

  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <>
      {/* Floating debug button */}
      <div className="fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsOpen(true)}
          className="bg-yellow-100 hover:bg-yellow-200 border-yellow-300 text-yellow-800"
          title="Copy JWT Token (Dev Only)"
        >
          <Key className="h-4 w-4" />
        </Button>
      </div>

      {/* Debug modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-lg font-semibold text-yellow-800">
                🔧 JWT Token Debug (Development Only)
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-sm text-gray-600">
                Copy this JWT token to test API calls with curl or other tools:
              </div>
              
              {jwtToken ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">JWT Token:</span>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={refreshToken}
                        className="flex items-center gap-2"
                      >
                        <RefreshCw className="h-4 w-4" />
                        Refresh
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyToClipboard}
                        className="flex items-center gap-2"
                      >
                        <Copy className="h-4 w-4" />
                        Copy
                      </Button>
                    </div>
                  </div>
                  <div className="bg-gray-100 p-3 rounded-md text-xs font-mono break-all max-h-32 overflow-y-auto">
                    {jwtToken}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
                  No JWT token found. Make sure you&apos;re logged in.
                </div>
              )}

              {debugInfo && (
                <div className="space-y-2">
                  <span className="text-sm font-medium">Debug Info:</span>
                  <div className="bg-blue-50 p-3 rounded-md text-xs font-mono whitespace-pre-line max-h-32 overflow-y-auto">
                    {debugInfo}
                  </div>
                </div>
              )}

              <div className="text-xs text-gray-500 space-y-1">
                <div><strong>Usage example:</strong></div>
                <div className="bg-gray-100 p-2 rounded font-mono text-xs">
                  curl -H &quot;Authorization: Bearer YOUR_JWT_TOKEN&quot; http://localhost:3000/api/admin/surveys
                </div>
              </div>

              <div className="text-xs text-gray-500">
                <strong>Note:</strong> This component only appears in development mode.
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
